'use client';

import { c as _c } from "react/compiler-runtime";
import * as RadixTabs from '@radix-ui/react-tabs';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { type CSSProperties, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { FADE_PX, getRevealScrollLeft } from './tabs.utils';
export type TabOption<T extends string = string> = {
  label: string;
  value: T;
};
export type TabsProps<T extends string = string> = {
  onChange: (value: T) => void;
  options: TabOption<T>[];
  disabled?: boolean;
  defaultValue?: T;
  /** Radix skips `onValueChange` for a trigger it believes is already active, so driving tabs from
   * code needs `value`. */
  value?: T;
};
const Tabs = t0 => {
  const $ = _c(34);
  const {
    onChange,
    options,
    disabled,
    defaultValue,
    value
  } = t0;
  const firstOptionValue = defaultValue ?? options[0].value;
  const [uncontrolled, setUncontrolled] = useState(firstOptionValue);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      start: false,
      end: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [overflow, setOverflow] = useState(t1);
  const scrollerRef = useRef(null);
  const activeTab = value ?? uncontrolled;
  const id = useId();
  let t2;
  if ($[1] !== onChange) {
    t2 = next => {
      setUncontrolled(next as T);
      onChange(next as T);
    };
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleChange = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      const start = scroller.scrollLeft > 0;
      const end = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      setOverflow(prev => prev.start === start && prev.end === end ? prev : {
        start,
        end
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const checkOverflow = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = tab => {
      const scroller_0 = scrollerRef.current;
      if (scroller_0 && tab) {
        scroller_0.scrollLeft = getRevealScrollLeft(scroller_0.getBoundingClientRect(), tab.getBoundingClientRect(), scroller_0.scrollLeft);
      }
      checkOverflow();
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const revealTab = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => revealTab(scrollerRef.current?.querySelector("[role=\"tab\"][data-state=\"active\"]"));
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const revealActiveTab = t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const scroller_1 = scrollerRef.current;
      if (!scroller_1?.firstElementChild) {
        return;
      }
      const observer = new ResizeObserver(revealActiveTab);
      observer.observe(scroller_1);
      observer.observe(scroller_1.firstElementChild);
      return () => observer.disconnect();
    };
    t7 = [];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== activeTab) {
    t8 = [activeTab];
    $[8] = activeTab;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  useLayoutEffect(revealActiveTab, t8);
  const t9 = value ? undefined : firstOptionValue;
  let t10;
  if ($[10] !== overflow.end || $[11] !== overflow.start) {
    t10 = cx("relative overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", {
      "mask-l-from-[calc(100%-var(--fade))]": overflow.start,
      "mask-r-from-[calc(100%-var(--fade))]": overflow.end
    });
    $[10] = overflow.end;
    $[11] = overflow.start;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      "--fade": `${FADE_PX}px`
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== activeTab || $[15] !== id || $[16] !== options) {
    let t13;
    if ($[18] !== activeTab || $[19] !== id) {
      t13 = t14 => {
        const {
          label,
          value: value_0
        } = t14;
        return <motion.div key={value_0} className="relative" layoutRoot={true} layout={true}><RadixTabs.Trigger value={value_0} onFocus={event => revealTab(event.currentTarget)} className="relative whitespace-nowrap text-body-2 py-1 my-1 px-1 mx-1 cursor-pointer data-[state=active]:text-accent data-[state=inactive]:text-tertiary rounded hover:data-[state=inactive]:bg-secondary transition-colors duration-100">{label}</RadixTabs.Trigger>{value_0 === activeTab && <motion.div initial={false} layoutId={id} transition={{
            duration: 0.15
          }} className="absolute -bottom-[1px] left-0 right-0 w-full h-[1px] bg-accent" />}</motion.div>;
      };
      $[18] = activeTab;
      $[19] = id;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    t12 = options.map(t13);
    $[14] = activeTab;
    $[15] = id;
    $[16] = options;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <motion.div className="relative flex w-max min-w-full border-b border-tertiary">{t12}</motion.div>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== disabled || $[24] !== t13) {
    t14 = <RadixTabs.TabsList data-is-disabled={disabled} asChild={true}>{t13}</RadixTabs.TabsList>;
    $[23] = disabled;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t14) {
    t15 = <div ref={scrollerRef} className={t10} style={t11 as CSSProperties} onScroll={checkOverflow}>{t14}</div>;
    $[26] = t10;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handleChange || $[30] !== t15 || $[31] !== t9 || $[32] !== value) {
    t16 = <RadixTabs.Root onValueChange={handleChange} value={value} defaultValue={t9}>{t15}</RadixTabs.Root>;
    $[29] = handleChange;
    $[30] = t15;
    $[31] = t9;
    $[32] = value;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
};
export default Tabs;